import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { money } from "@/lib/api";
import { StatusBadge, Panel, fmt12 } from "./AdminUI";

const DAY = 864e5;
const FILTERS = [
  ["new", "New (last 24h)", (b) => b.status === "pending" && Date.now() - new Date(b.created_at) < DAY],
  ["pending", "Pending approvals", (b) => b.status === "pending"],
  ["confirmed", "Confirmed", (b) => b.status === "confirmed"],
  ["completed", "Completed", (b) => b.status === "completed"],
  ["canceled", "Canceled", (b) => b.status === "canceled" || b.status === "rejected"],
  ["all", "Booking history", () => true],
];

export const BookingsTable = ({ rows, onOpen }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[760px] text-left text-sm" data-testid="bookings-table">
      <thead className="text-xs uppercase tracking-wide text-slate-400">
        <tr><th className="py-2">Ref</th><th>Date</th><th>Service</th><th>Customer</th><th>Property</th><th>Total</th><th>Status</th></tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.reference} onClick={() => onOpen(b)} data-testid={`booking-row-${b.reference}`} className="cursor-pointer border-t border-slate-100 hover:bg-[#FAF8F1]">
            <td className="py-3 font-mono-x text-xs">{b.reference}</td>
            <td>{b.date} {fmt12(b.time)}</td>
            <td>{b.service_name}</td>
            <td>{b.full_name}<br /><span className="text-xs text-slate-400">{b.phone}</span></td>
            <td className="max-w-[200px] truncate">{b.address}, {b.city} {b.zip}</td>
            <td>{money(b.quote.total)}</td>
            <td><StatusBadge status={b.status} /></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No bookings here yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export const BookingsPanel = ({ bookings, onOpen }) => {
  const [filter, setFilter] = useState("pending");
  const fn = FILTERS.find((x) => x[0] === filter)[2];
  return (
    <Panel title="Bookings">
      <div className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map(([k, label, f]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${filter === k ? "bg-[#0B132B] text-white" : "bg-[#F5F3EC] text-slate-600"}`}>
            {label} <span className="ml-1 opacity-70">{bookings.filter(f).length}</span>
          </button>
        ))}
      </div>
      <BookingsTable rows={bookings.filter(fn)} onOpen={onOpen} />
    </Panel>
  );
};

export const CalendarPanel = ({ bookings, onOpen }) => {
  const [day, setDay] = useState(new Date());
  const active = useMemo(() => bookings.filter((b) => b.status !== "canceled" && b.status !== "rejected"), [bookings]);
  const dates = useMemo(() => [...new Set(active.map((b) => b.date))].map((d) => new Date(`${d}T12:00:00`)), [active]);
  const key = format(day, "yyyy-MM-dd");
  const rows = active.filter((b) => b.date === key).sort((a, b) => a.start_min - b.start_min);
  return (
    <Panel title="Appointment calendar">
      <div className="grid gap-6 md:grid-cols-[auto_1fr]">
        <div data-testid="admin-calendar" className="rounded-xl border border-slate-200">
          <Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} modifiers={{ booked: dates }}
            modifiersClassNames={{ booked: "font-bold text-gold underline decoration-2 underline-offset-4" }} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0B132B]">{format(day, "EEEE, MMMM d")}</p>
          <ul className="mt-3 space-y-2">
            {rows.map((b) => (
              <li key={b.reference}>
                <button type="button" onClick={() => onOpen(b)} data-testid={`calendar-item-${b.reference}`}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left text-sm hover:border-gold">
                  <span><b>{fmt12(b.time)}</b> · {b.service_name}<br /><span className="text-slate-500">{b.full_name} · {b.address}, {b.city}</span></span>
                  <StatusBadge status={b.status} />
                </button>
              </li>
            ))}
            {!rows.length && <li className="text-sm text-slate-400">No appointments this day.</li>}
          </ul>
        </div>
      </div>
    </Panel>
  );
};
